/**
 * The shared search index: one builder and one ranker consumed by the browser
 * search page, the service and the per-audience generated artifacts under
 * framework/generated/search/.
 *
 * Audience isolation is by construction: the only content inputs are one
 * already-projected snapshot plus the site-audience downloads manifest, so
 * material the resolver removed cannot exist in any artifact. Determinism is
 * a contract: no timestamps, no randomness, no locale-sensitive comparison in
 * artifact content; postings are sorted numerically and every map serialises
 * in sorted key order through the repository's stableStringify.
 */
import { buildPartnerCapabilityReverseIndex } from "./capability-mappings";
import type {
  AudienceId,
  FrameworkSnapshot,
  ResolvedPartnerCapabilityMapping,
} from "./types";

export type SearchCopyResolver = (key: string, fallback?: string) => string;

/** Result group identity shared by the artifact, the page and the tests. */
export const SEARCH_GROUP = {
  pages: "pages",
  workblocks: "workblocks",
  businessModules: "business-modules",
  executionFlows: "execution-flows",
  roles: "roles",
  workMoments: "work-moments",
  groundworksRecords: "groundworks-records",
  connectedWork: "connected-work",
  documents: "documents",
  officeCapabilities: "office-capabilities",
  faqs: "faqs",
  language: "language",
  partnerCapabilityAreas: "partner-capability-areas",
  partnerCapabilities: "partner-capabilities",
} as const;

export interface SearchDocument {
  key: string;
  group: string;
  label: string;
  description?: string;
  /** Additional matchable text that never renders: outcomes, aliases, vendor names. */
  keywords?: string;
  /** Short placement line rendered under the description. */
  context?: string;
  to: string;
  icon?: string;
  /** Workblock implementation status, rendered as its display chip beside the label. */
  status?: string;
}

/** A downloadable-asset document: postings carry its body, the excerpt renders. */
export interface AssetSearchDocument {
  key: string;
  label: string;
  /** At most 400 characters of normalised body text; the only rendered body content. */
  excerpt: string;
  context: string;
  to: string;
  icon: string;
}

/** Generator-side input describing one downloadable asset's text payloads. */
export interface SearchAssetInput {
  assetId: string;
  assetClass: string;
  title: string;
  route: string;
  texts: string[];
}

export interface SearchIndexArtifact {
  doNotEdit: true;
  schemaVersion: string;
  audienceId: AudienceId;
  /** Binds the artifact to its snapshot's generated.contentHash for staleness checks. */
  sourceContentHash: string;
  documents: SearchDocument[];
  assetDocuments: AssetSearchDocument[];
  /** Sorted token to ascending assetDocuments indexes; token and prefix matching only. */
  assetTokens: Record<string, number[]>;
  /**
   * Exact-term tier: normalised term to the sorted set of owning document
   * keys. Mapping paths and entity, module, flow, role and asset ids resolve
   * deterministically; field ids are many-to-many (156 of 646 are reused
   * across entities) and return the ranked owning-entity set, never one row.
   */
  exact: Record<string, string[]>;
}

export interface SearchIndexMatch {
  entry: SearchDocument;
  score: number;
}

export const searchIndexSchemaVersion = "1.0.0";

/**
 * The exact-term tier scores above every substring hit: queries are capped at
 * 120 characters, so a lexical score can never reach this bonus.
 */
export const exactTermScore = 1000;

export const searchExcerptLimit = 400;

/* ------------------------------------------------------------------------ *
 * Catalogue selectors, carried verbatim from the application so the builder
 * emits byte-identical documents to the retired in-page index. The canonical
 * route helpers stay one-line mirrors of apps/ux-app/src/catalogue.ts.
 * ------------------------------------------------------------------------ */

type AnyBusinessModule = FrameworkSnapshot["businessModuleRegistry"]["businessModules"][number];
type AnyExecutionFlow = AnyBusinessModule["executionFlows"][number];

function isInternalBusinessModule(module: AnyBusinessModule): boolean {
  return "roadmap" in module && "learningLoop" in module;
}

function isInternalExecutionFlow(flow: AnyExecutionFlow): boolean {
  return "tasks" in flow && "inputs" in flow && "outputs" in flow && "stateSurfaces" in flow;
}

const domainWorkblockPath = (domainWorkblockId: string): string => `/workblocks/${domainWorkblockId}`;
const businessModulePath = (domainWorkblockId: string, businessModuleId: string): string =>
  `${domainWorkblockPath(domainWorkblockId)}/business-modules/${businessModuleId}`;
const rolePath = (roleId: string): string => `/reference/roles/${encodeURIComponent(roleId)}`;
const workMomentPath = (momentId: string): string => `/reference/work-moments/${encodeURIComponent(momentId)}`;
const integrationRecipePath = (recipeId: string): string => `/connected-work/recipes/${encodeURIComponent(recipeId)}`;
const integrationExamplePath = (exampleId: string): string => `/connected-work/examples/${encodeURIComponent(exampleId)}`;
const integrationToolPath = (toolId: string): string => `/connected-work/tools/${encodeURIComponent(toolId)}`;
const integrationSystemPath = (systemId: string): string => `/connected-work/systems/${encodeURIComponent(systemId)}`;
const integrationMappingPath = (mappingId: string): string => `/connected-work/mappings/${encodeURIComponent(mappingId)}`;
const groundworksEntityPath = (entityId: string): string => `/groundworks/${encodeURIComponent(entityId)}`;
const officeCapabilityPath = (productId: string): string => `/capabilities/${encodeURIComponent(productId)}`;
const downloadAssetPath = (assetId: string): string => `/downloads/${encodeURIComponent(assetId)}`;

const humanizeAcronyms = new Set([
  "AI", "API", "CLM", "DOCX", "ERP", "ESG", "ISO", "KPI", "P2P",
  "RFI", "RFP", "RFQ", "S2P", "SLA", "SOC", "TPRM", "XLSX",
]);

function humanize(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const uppercase = word.toUpperCase();
      if (humanizeAcronyms.has(uppercase)) return uppercase;
      const lowercase = word.toLowerCase();
      return index === 0 ? `${lowercase.charAt(0).toUpperCase()}${lowercase.slice(1)}` : lowercase;
    })
    .join(" ");
}

/** Section anchors mirrored from apps/ux-app/src/pages/LanguagePage.tsx languageSections. */
const languageSearchSections = [
  { id: "registers", headingKey: "page.language.registers.heading", descriptionKey: "page.language.registers.description", keywordsKey: "page.language.registers.keywords" },
  { id: "audiences", headingKey: "page.language.audiences.heading", descriptionKey: "page.language.audiences.description", keywordsKey: "page.language.audiences.keywords" },
  { id: "jobs", headingKey: "page.language.jobs.heading", descriptionKey: "page.language.jobs.description", keywordsKey: "page.language.jobs.keywords" },
  { id: "mechanism", headingKey: "page.language.mechanism.heading", descriptionKey: "page.language.mechanism.description", keywordsKey: "page.language.mechanism.keywords" },
  { id: "vocabulary", headingKey: "page.language.vocabulary.heading", descriptionKey: "page.language.vocabulary.description", keywordsKey: "page.language.vocabulary.keywords" },
  { id: "terms", headingKey: "page.language.terms.heading", descriptionKey: "page.language.terms.description", keywordsKey: "page.language.terms.keywords" },
  { id: "refusals", headingKey: "page.language.refusals.heading", descriptionKey: "page.language.refusals.description", keywordsKey: "page.language.refusals.keywords" },
  { id: "patterns", headingKey: "page.language.patterns.heading", descriptionKey: "page.language.patterns.description", keywordsKey: "page.language.patterns.keywords" },
  { id: "glossary", headingKey: "page.glossary.title", descriptionKey: "page.glossary.description", keywordsKey: "page.language.glossary.keywords" },
] as const;

interface RolePlacementEntry {
  domainWorkblock: FrameworkSnapshot["content"]["domainWorkblocks"][number];
  role: FrameworkSnapshot["content"]["domainWorkblocks"][number]["roleCards"][number];
}

function roleCatalogue(snapshot: FrameworkSnapshot): Array<{
  definition: NonNullable<FrameworkSnapshot["content"]["roles"]>[number];
  placements: RolePlacementEntry[];
}> {
  const placementsById = new Map<string, RolePlacementEntry[]>();
  for (const domainWorkblock of snapshot.content.domainWorkblocks) {
    for (const role of domainWorkblock.roleCards) {
      placementsById.set(role.id, [...(placementsById.get(role.id) ?? []), { domainWorkblock, role }]);
    }
  }
  return (snapshot.content.roles ?? []).flatMap((definition) => {
    const placements = placementsById.get(definition.id) ?? [];
    return placements.length ? [{ definition, placements }] : [];
  });
}

function workMomentCatalogue(snapshot: FrameworkSnapshot): Array<{
  definition: NonNullable<FrameworkSnapshot["content"]["workMoments"]>[number];
  businessModule: AnyBusinessModule;
  domainWorkblock: FrameworkSnapshot["content"]["domainWorkblocks"][number];
}> {
  return (snapshot.content.workMoments ?? []).flatMap((definition) => {
    const businessModule = snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === definition.businessModuleId);
    if (!businessModule) return [];
    const domainWorkblock = snapshot.content.domainWorkblocks.find(({ id }) => id === businessModule.domainWorkblockId);
    if (!domainWorkblock) return [];
    const perspective =
      definition.perspectives.find((candidate) => candidate.roleId === definition.defaultPerspectiveRoleId) ??
      definition.perspectives[0];
    const jobStory = perspective
      ? (businessModule.jobStories ?? []).find(({ id }) => id === perspective.jobId)
      : undefined;
    const agent = snapshot.content.workMomentAgents?.find(({ id }) => id === definition.agentId);
    const fixture = snapshot.content.workMomentFixtures?.find(({ id }) => id === definition.fixtureId);
    return perspective && jobStory && agent && fixture ? [{ definition, businessModule, domainWorkblock }] : [];
  });
}

function partnerCapabilityAreas(mapping: ResolvedPartnerCapabilityMapping) {
  return mapping.nodes
    .filter((node) => node.kind !== "capability")
    .sort((left, right) => left.order - right.order || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}

function derivePartnerCapabilityCoverage(
  snapshot: FrameworkSnapshot,
  node: ResolvedPartnerCapabilityMapping["nodes"][number],
) {
  const moduleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const modules = node.groundworks.businessModuleIds.flatMap((id) => {
    const module = moduleById.get(id);
    return module
      ? [{ id: module.id, labelKey: module.labelKey, implementationStatus: module.implementationStatus }]
      : [];
  });
  const statuses = new Set(modules.map(({ implementationStatus }) => implementationStatus));
  const hasModuleTarget = node.groundworks.businessModuleIds.length > 0;
  const status = statuses.has("reference")
    ? "reference"
    : statuses.has("pattern")
      ? "pattern"
      : hasModuleTarget
        ? "roadmap"
        : "unmapped";
  return { status, modules, isMixed: statuses.size > 1 };
}

function capabilityMapHref({ mapId, anchor }: { mapId: string; anchor: string }): string {
  const parameters = new URLSearchParams({ view: "partner" });
  parameters.set("map", mapId);
  return `/capability-map?${parameters.toString()}#${anchor}`;
}

function partnerCapabilityLinksForTarget(
  snapshot: FrameworkSnapshot,
  copy: SearchCopyResolver,
  kind: "domain-workblock" | "business-module",
  targetId: string,
): Array<{ label: string; context?: string }> {
  return (snapshot.partnerCapabilityMappings ?? []).flatMap((mapping) => {
    const index = buildPartnerCapabilityReverseIndex(mapping);
    const nodes = kind === "domain-workblock" ? index.byDomainWorkblockId[targetId] : index.byBusinessModuleId[targetId];
    const areaById = new Map(partnerCapabilityAreas(mapping).map((area) => [area.id, area]));
    return (nodes ?? []).map((node) => {
      const area = areaById.get(node.parentId ?? "");
      return { label: copy(node.labelKey), context: area ? copy(area.labelKey) : undefined };
    });
  });
}

/* ------------------------------------------------------------------------ *
 * The core snapshot documents: the in-page buildIndex moved here verbatim.
 * ------------------------------------------------------------------------ */

export function buildSnapshotDocuments(
  snapshot: FrameworkSnapshot,
  copy: SearchCopyResolver,
): SearchDocument[] {
  const entries: SearchDocument[] = [];
  const { content } = snapshot;
  const domainWorkblockById = new Map(content.domainWorkblocks.map((workblock) => [workblock.id, workblock]));
  const statusById = new Map(content.businessModuleStatusDisplay.map((status) => [status.id, status]));

  const dataModelRoute = content.routes.find(
    (route) => route.id === "data-model" && route.path === "/data-model",
  );
  if (dataModelRoute) {
    entries.push({
      key: "page-data-model",
      group: SEARCH_GROUP.pages,
      label: copy(dataModelRoute.labelKey),
      description: copy("page.data-model.description"),
      keywords: [
        dataModelRoute.id,
        dataModelRoute.path,
        ...Object.entries(snapshot.copy).flatMap(([key, value]) =>
          key.startsWith("page.data-model.") ? [value] : [],
        ),
        ...snapshot.groundworks.dimensions.flatMap((dimension) => [
          dimension.id,
          copy(dimension.labelKey),
          copy(dimension.definitionKey),
          ...dimension.members.flatMap((member) => [
            member.id,
            copy(member.labelKey),
            copy(member.definitionKey),
          ]),
        ]),
      ].join(" "),
      context: copy("page.data-model.eyebrow"),
      to: dataModelRoute.path,
      icon: dataModelRoute.icon,
    });
  }

  /* Governed editorial stories: the whole story record is matchable, and the
   * result lands on the story's dedicated Client engagement route. */
  for (const story of content.editorialStories ?? []) {
    const storyRoute = content.routes.find((route) => route.id === story.id);
    if (!storyRoute) continue;
    entries.push({
      key: `editorial-story-${story.id}`,
      group: SEARCH_GROUP.pages,
      label: copy(story.titleKey),
      description: copy(story.summaryKey),
      keywords: [
        story.id,
        copy(story.kickerKey),
        copy(story.outcomeKey),
        copy(story.systemBoundaryKey),
        ...story.agentIds,
        ...story.executionFlowIds,
        ...story.systemProfileIds,
        ...story.chapters.flatMap((chapter) => [
          copy(chapter.titleKey),
          copy(chapter.bodyKey),
          copy(chapter.authorityKey),
          copy(chapter.handoffKey),
        ]),
      ].join(" "),
      context: copy(story.kickerKey),
      to: storyRoute.path,
      icon: storyRoute.icon,
    });
  }

  const designRoute = content.routes.find(
    (route) => route.id === "design" && route.path === "/design",
  );
  if (designRoute) {
    const narrative = snapshot.designNarrative;
    entries.push({
      key: "page-design",
      group: SEARCH_GROUP.pages,
      label: copy(designRoute.labelKey),
      description: copy("page.design.description"),
      keywords: [
        designRoute.id,
        designRoute.path,
        copy("page.design.eyebrow"),
        copy("page.design.title"),
        ...narrative.anchors.flatMap((anchor) => [anchor.id, copy(anchor.labelKey)]),
        ...narrative.principles.flatMap((principle) => [
          principle.id,
          copy(principle.titleKey),
          copy(principle.bodyKey),
          copy(principle.choiceTitleKey),
          copy(principle.choiceBodyKey),
        ]),
        ...narrative.componentCatalogue.componentIds,
      ].join(" "),
      context: copy("page.design.eyebrow"),
      to: designRoute.path,
      icon: designRoute.icon,
    });
  }

  const architectureRoute = content.routes.find((route) => route.id === "architecture");
  if (architectureRoute) {
    /* Cross-cutting capabilities and coexistence landscapes: search coverage
     * restored after the dea62e29 group-taxonomy cut (2026-08-26 audit). */
    for (const capability of content.capabilities)
      entries.push({
        key: `capability-${capability.id}`,
        group: SEARCH_GROUP.pages,
        label: copy(capability.labelKey),
        description: copy(capability.descriptionKey),
        keywords: capability.id,
        context: copy(architectureRoute.labelKey),
        to: `/architecture#${capability.id}`,
        icon: capability.icon,
      });
    for (const landscape of content.landscapes)
      entries.push({
        key: `landscape-${landscape.id}`,
        group: SEARCH_GROUP.pages,
        label: copy(landscape.labelKey),
        description: copy(landscape.descriptionKey),
        keywords: [landscape.id, landscape.kind, ...landscape.domainWorkblockIds].join(" "),
        context: copy(architectureRoute.labelKey),
        to: `/architecture#${landscape.id}`,
        icon: architectureRoute.icon,
      });
  }

  const whyGroundworksRoute = content.routes.find((route) => route.id === "why-groundworks");
  if (whyGroundworksRoute) {
    /* The enterprise message house: the flagship commercial argument gains
     * per-message documents on its own route (2026-08-26 audit). */
    for (const message of content.enterpriseMessages ?? [])
      entries.push({
        key: `enterprise-message-${message.id}`,
        group: SEARCH_GROUP.pages,
        label: copy(message.titleKey),
        description: copy(message.headlineKey),
        keywords: [message.id, message.category, copy(message.buyerValueKey), copy(message.mechanismKey)].join(" "),
        context: copy(whyGroundworksRoute.labelKey),
        to: `/why-groundworks#${message.id}`,
        icon: whyGroundworksRoute.icon,
      });
  }

  const groundworksRoute = content.routes.find((route) => route.id === "groundworks");
  if (groundworksRoute) {
    /* The registry explorer page document, mirroring page-data-model
     * (2026-08-26 audit: heaviest governed page had no search landing). */
    entries.push({
      key: "page-groundworks",
      group: SEARCH_GROUP.pages,
      label: copy(groundworksRoute.labelKey),
      description: copy("page.groundworks.title"),
      keywords: [
        groundworksRoute.id,
        groundworksRoute.path,
        ...Object.entries(snapshot.copy).flatMap(([key, value]) =>
          key.startsWith("page.groundworks.") ? [value] : [],
        ),
      ].join(" "),
      context: copy(groundworksRoute.labelKey),
      to: groundworksRoute.path,
      icon: groundworksRoute.icon,
    });
  }

  for (const domainWorkblock of content.domainWorkblocks) {
    entries.push({
      key: `workblock-${domainWorkblock.id}`,
      group: SEARCH_GROUP.workblocks,
      label: copy(domainWorkblock.nameKey),
      description: copy(domainWorkblock.descriptorKey),
      keywords: [
        domainWorkblock.signatureKey ? copy(domainWorkblock.signatureKey) : "",
        copy(domainWorkblock.labelKey),
        copy(domainWorkblock.shortLabelKey),
        copy(domainWorkblock.descriptionKey),
        copy(domainWorkblock.outcomeKey),
        ...partnerCapabilityLinksForTarget(snapshot, copy, "domain-workblock", domainWorkblock.id)
          .map(({ label, context }) => `${context ?? ""} ${label}`),
      ].join(" "),
      to: domainWorkblockPath(domainWorkblock.id),
      icon: domainWorkblock.icon,
    });
  }

  for (const { definition, placements } of roleCatalogue(snapshot))
    entries.push({
      key: `role-${definition.id}`,
      group: SEARCH_GROUP.roles,
      label: copy(definition.labelKey),
      description: copy(definition.descriptionKey),
      keywords: [
        ...(definition.searchTermKeys ?? []).map((key) => copy(key)),
        ...placements.flatMap(({ domainWorkblock, role }) => [
          copy(domainWorkblock.nameKey),
          copy(role.roleKey),
          copy(role.mandateKey),
          copy(role.questionKey),
          copy(role.measuredKey),
        ]),
      ].join(" "),
      context: placements
        .map(({ domainWorkblock }) => copy(domainWorkblock.nameKey))
        .join(" · "),
      to: rolePath(definition.id),
    });

  for (const { definition, businessModule, domainWorkblock } of workMomentCatalogue(snapshot))
    entries.push({
      key: `work-moment-${definition.id}`,
      group: SEARCH_GROUP.workMoments,
      label: copy(definition.titleKey),
      description: copy(definition.summaryKey),
      keywords: definition.perspectives
        .flatMap((perspective) => [
          perspective.roleId,
          copy(perspective.responsibilityKey),
          copy(perspective.beforeKey),
          copy(perspective.nowKey),
        ])
        .join(" "),
      context: `${copy(businessModule.labelKey)} · ${copy(domainWorkblock.nameKey)}`,
      to: workMomentPath(definition.id),
    });

  for (const businessModule of snapshot.businessModuleRegistry.businessModules) {
    const domainWorkblock = domainWorkblockById.get(businessModule.domainWorkblockId);
    const status = statusById.get(businessModule.implementationStatus);
    entries.push({
      key: `business-module-${businessModule.id}`,
      group: SEARCH_GROUP.businessModules,
      label: copy(businessModule.labelKey),
      description: copy(businessModule.jobStatementKey),
      keywords: [
        copy(businessModule.descriptionKey),
        copy(businessModule.outcomeKey),
        copy(businessModule.toolKey),
        copy(businessModule.hiredInsteadKey),
        businessModule.agentStoryKey ? copy(businessModule.agentStoryKey) : "",
        /* Published agent charters: label and mission are the discoverable
         * public contract; the match lands on the module's agents section. */
        ...businessModule.agents.flatMap((agent) => [agent.id, agent.label, agent.mission]),
        ...(isInternalBusinessModule(businessModule)
          ? (businessModule as { features: Array<{ group: string; items: string[] }> }).features
              .flatMap((feature) => [feature.group, ...feature.items])
          : []),
        ...(businessModule.jobStories ?? []).flatMap((story) => [
          story.id,
          story.roleCardId ?? "",
          copy(story.storyKey),
          copy(story.completionKey),
        ]),
        domainWorkblock ? copy(domainWorkblock.nameKey) : "",
        status ? copy(status.chipKey) : businessModule.implementationStatus,
        ...partnerCapabilityLinksForTarget(snapshot, copy, "business-module", businessModule.id)
          .map(({ label, context }) => `${context ?? ""} ${label}`),
      ].join(" "),
      to: businessModulePath(businessModule.domainWorkblockId, businessModule.id),
      icon: "blocks",
      status: businessModule.implementationStatus,
    });
    for (const flow of businessModule.executionFlows) {
      entries.push({
        key: `execution-flow-${businessModule.id}-${flow.id}`,
        group: SEARCH_GROUP.executionFlows,
        label: flow.label,
        description: isInternalExecutionFlow(flow)
          ? (flow as { jobToBeDone: { statement: string } }).jobToBeDone.statement
          : flow.trigger,
        keywords: [
          flow.id,
          flow.kind,
          flow.trigger,
          ...flow.jobToBeDone.roleCardIds,
          ...flow.jobToBeDone.completionEvidence,
          ...(isInternalExecutionFlow(flow)
            ? [
                ...(flow as { inputs: string[] }).inputs,
                ...(flow as { outputs: string[] }).outputs,
                ...(flow as { stateSurfaces: string[] }).stateSurfaces,
                ...(flow as { tasks: Array<{ id: string; label: string; inputs: string[]; outputs: string[] }> }).tasks
                  .flatMap((task) => [task.id, task.label, ...task.inputs, ...task.outputs]),
              ]
            : []),
        ].join(" "),
        context: `${copy(businessModule.labelKey)} · ${domainWorkblock ? copy(domainWorkblock.nameKey) : businessModule.domainWorkblockId}`,
        to: `${businessModulePath(businessModule.domainWorkblockId, businessModule.id)}#flow-${flow.id}`,
        icon: "git-branch",
      });
    }
  }

  for (const entity of snapshot.groundworks.entities)
    entries.push({
      key: `groundworks-record-${entity.id}`,
      group: SEARCH_GROUP.groundworksRecords,
      label: entity.label,
      description: entity.definition,
      keywords: [
        entity.id,
        entity.pluralLabel,
        entity.kind,
        ...entity.searchTerms,
        ...entity.domainWorkblockIds,
        entity.ownerBusinessModuleId ?? "",
        ...entity.consumerBusinessModuleIds,
        ...entity.fields.flatMap((field) => [
          field.id,
          field.label,
          field.definition,
          field.sourceAuthority?.id ?? "",
          ...field.standardMappings.flatMap((mapping) => [
            mapping.standardId,
            mapping.documentId ?? mapping.conceptId ?? "",
            mapping.path,
          ]),
        ]),
      ].join(" "),
      context: humanize(entity.storageClass),
      to: groundworksEntityPath(entity.id),
      icon: "database",
    });

  const downloadsRoute = content.routes.find((route) => route.path === "/downloads");
  if (downloadsRoute) {
    entries.push({
      key: "page-downloads",
      group: SEARCH_GROUP.pages,
      label: copy(downloadsRoute.labelKey),
      description: copy("page.downloads.body"),
      keywords: [
        downloadsRoute.id,
        downloadsRoute.path,
        copy("page.downloads.headline"),
        ...Object.entries(snapshot.copy).flatMap(([key, value]) =>
          key.startsWith("page.downloads.group.") ? [value] : [],
        ),
      ].join(" "),
      context: copy("page.downloads.eyebrow"),
      to: downloadsRoute.path,
      icon: downloadsRoute.icon,
    });
  }

  const integrations = snapshot.integrations;
  for (const recipe of integrations.recipes)
    entries.push({
      key: `integration-recipe-${recipe.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: recipe.label,
      description: recipe.outcome,
      keywords: [
        recipe.id,
        recipe.trigger,
        recipe.owner,
        recipe.direction,
        ...recipe.channelIds,
        ...recipe.formatIds,
        ...recipe.systemRoleIds,
        ...recipe.domainWorkblockIds,
        ...recipe.businessModuleIds,
      ].join(" "),
      context: copy("page.search.kind.integration-recipe"),
      to: integrationRecipePath(recipe.id),
      icon: "plug",
    });
  for (const tool of integrations.toolRecipes)
    entries.push({
      key: `integration-tool-${tool.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: tool.label,
      description: tool.outcome,
      keywords: [tool.id, tool.businessModuleId, tool.actionClass, ...tool.recipeIds, ...tool.entityIds].join(" "),
      context: copy("page.search.kind.outcome-tool"),
      to: integrationToolPath(tool.id),
      icon: "sparkles",
    });
  for (const example of integrations.examples)
    entries.push({
      key: `integration-example-${example.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: example.label,
      description: example.summary,
      keywords: [
        example.id,
        example.userQuestion ?? "",
        example.output ?? "",
        example.owner,
        ...example.recipeIds,
        ...example.entityIds,
      ].join(" "),
      context: copy("page.search.kind.illustrative-example"),
      to: integrationExamplePath(example.id),
      icon: "box",
    });
  for (const system of integrations.systemProfiles)
    entries.push({
      key: `integration-system-${system.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: system.name,
      description: system.designQuestion,
      keywords: [system.id, system.familyId, ...system.systemRoleIds, ...system.recipeIds].join(" "),
      context: copy("page.search.kind.system-profile"),
      to: integrationSystemPath(system.id),
      icon: "database",
    });
  for (const profile of integrations.mappingProfiles)
    entries.push({
      key: `integration-mapping-${profile.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: profile.label,
      description: profile.mappingBoundary,
      keywords: [profile.id, ...profile.entityIds].join(" "),
      context: copy("page.search.kind.mapping-profile"),
      to: integrationMappingPath(profile.id),
      icon: "map",
    });

  for (const format of integrations.formats)
    entries.push({
      key: `integration-format-${format.id}`,
      group: SEARCH_GROUP.connectedWork,
      label: format.label,
      description: format.qualifiedId,
      keywords: [format.id, format.kind, format.standardId, ...(format.conformanceStandardIds ?? [])].join(" "),
      context: format.kind,
      to: `/connected-work/standards/${format.id}`,
      icon: "plug",
    });

  for (const product of content.officeProducts ?? [])
    entries.push({
      key: `office-capability-${product.id}`,
      group: SEARCH_GROUP.officeCapabilities,
      label: copy(product.nameKey),
      description: copy(product.taglineKey),
      keywords: [
        product.id,
        copy(product.formatKey),
        copy(product.summaryKey),
        copy(product.thesisKey),
      ].join(" "),
      context: copy("page.search.kind.office-capability"),
      to: officeCapabilityPath(product.id),
      icon: product.icon,
    });

  for (const faq of content.faqs)
    entries.push({
      key: `faq-${faq.id}`,
      group: SEARCH_GROUP.faqs,
      label: copy(faq.questionKey),
      description: copy(faq.answerKey),
      keywords: faq.category,
      context: faq.category,
      to: `/faqs#${faq.id}`,
      icon: "circle-help",
    });

  for (const vocabularyEntry of snapshot.groundworks.vocabulary)
    entries.push({
      key: `registry-vocabulary-${vocabularyEntry.id}`,
      group: SEARCH_GROUP.language,
      label: vocabularyEntry.term,
      description: vocabularyEntry.definition,
      keywords: [vocabularyEntry.id, ...vocabularyEntry.aliases].join(" "),
      context: copy("page.groundworks.title"),
      to: "/groundworks?section=registry-vocabulary",
      icon: "book-open",
    });

  for (const section of languageSearchSections)
    entries.push({
      key: `language-${section.id}`,
      group: SEARCH_GROUP.language,
      label: copy(section.headingKey),
      description: copy(section.descriptionKey),
      keywords: copy(section.keywordsKey),
      context: copy("page.language.eyebrow"),
      to: `/language#${section.id}`,
      icon: "book-open",
    });

  for (const mapping of snapshot.partnerCapabilityMappings ?? []) {
    const areas = partnerCapabilityAreas(mapping);
    const areaById = new Map(areas.map((area) => [area.id, area]));
    for (const area of areas)
      entries.push({
        key: `partner-capability-area-${mapping.id}-${area.id}`,
        group: SEARCH_GROUP.partnerCapabilityAreas,
        label: copy(area.labelKey),
        description: copy(mapping.titleKey),
        to: `/capability-map?view=partner&map=${encodeURIComponent(mapping.id)}#area-${area.id}`,
        icon: "map",
      });
    for (const node of mapping.nodes.filter((candidate) => candidate.kind === "capability")) {
      const area = areaById.get(node.parentId ?? "");
      const coverage = derivePartnerCapabilityCoverage(snapshot, node);
      const coverageLabel =
        coverage.status === "reference"
          ? copy("status.reference.chip")
          : coverage.status === "pattern"
            ? copy("status.pattern.chip")
            : copy(`partner-capability.overview.status.${coverage.status}.label`);
      entries.push({
        key: `partner-capability-${mapping.id}-${node.id}`,
        group: SEARCH_GROUP.partnerCapabilities,
        label: copy(node.labelKey),
        description: area ? copy(area.labelKey) : copy(mapping.titleKey),
        keywords: [
          copy(mapping.titleKey),
          node.groundworks.fit,
          coverageLabel,
          coverage.isMixed ? copy("partner-capability.overview.detail.mixed") : "",
          ...coverage.modules.map((module) => copy(module.labelKey)),
        ].join(" "),
        to: `/capability-map?view=partner&map=${encodeURIComponent(mapping.id)}#capability-${node.id}`,
        icon: "target",
      });
    }
  }

  return entries;
}

/* ------------------------------------------------------------------------ *
 * Corpus additions decided by the 2026-08-19 search memo: per-glossary-term
 * documents with canonical routes, and per-chapter editorial documents.
 * ------------------------------------------------------------------------ */

export function buildGlossaryTermDocuments(
  snapshot: FrameworkSnapshot,
  copy: SearchCopyResolver,
): SearchDocument[] {
  return snapshot.content.glossary.map((entry) => ({
    key: `glossary-term-${entry.id}`,
    group: SEARCH_GROUP.language,
    label: copy(entry.termKey),
    description: copy(entry.definitionKey),
    keywords: entry.id,
    context: copy("page.glossary.title"),
    to: `/language#glossary-${entry.id}`,
    icon: "book-open",
  }));
}

export function buildEditorialChapterDocuments(
  snapshot: FrameworkSnapshot,
  copy: SearchCopyResolver,
): SearchDocument[] {
  const entries: SearchDocument[] = [];
  for (const story of snapshot.content.editorialStories ?? []) {
    const storyRoute = snapshot.content.routes.find((route) => route.id === story.id);
    if (!storyRoute) continue;
    for (const chapter of story.chapters) {
      entries.push({
        key: `editorial-chapter-${story.id}-${chapter.id}`,
        group: SEARCH_GROUP.pages,
        label: copy(chapter.titleKey),
        description: copy(chapter.bodyKey),
        keywords: [
          chapter.id,
          copy(chapter.authorityKey),
          copy(chapter.handoffKey),
          chapter.ownerRoleId,
          ...chapter.businessModuleIds,
          ...chapter.executionFlowIds,
          ...chapter.recordEntityIds,
        ].join(" "),
        context: copy(story.titleKey),
        to: `${storyRoute.path}#chapter-${chapter.id}`,
        icon: storyRoute.icon,
      });
    }
  }
  return entries;
}

/* ------------------------------------------------------------------------ *
 * Tokeniser, excerpts and the artifact builder.
 * ------------------------------------------------------------------------ */

const tokenSeparator = /[^a-z0-9._:/-]+/;
const tokenTrim = /^[._:/-]+|[._:/-]+$/g;
const tokenSplit = /[._:/-]+/;

/** Sorted unique tokens plus their separator-split parts; prefix matching over these is the only body matching supported. */
export function tokenizeSearchText(text: string): string[] {
  const tokens = new Set<string>();
  for (const raw of text.toLowerCase().split(tokenSeparator)) {
    const trimmed = raw.replace(tokenTrim, "");
    if (trimmed.length < 2 || trimmed.length > 80) continue;
    tokens.add(trimmed);
    for (const part of trimmed.split(tokenSplit)) {
      if (part.length >= 2 && part.length <= 80) tokens.add(part);
    }
  }
  return [...tokens].sort();
}

/** Normalised body excerpt, at most searchExcerptLimit characters, cut on a word boundary. */
export function buildExcerpt(text: string, limit = searchExcerptLimit): string {
  const cleaned = text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`+/g, " ")
    .replace(/^#{1,6}\s+/gm, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length <= limit) return cleaned;
  const cut = cleaned.slice(0, limit);
  const boundary = cut.lastIndexOf(" ");
  return (boundary > limit / 2 ? cut.slice(0, boundary) : cut).trimEnd();
}

function addExact(exact: Map<string, Set<string>>, term: string, docKey: string): void {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return;
  const existing = exact.get(normalized) ?? new Set<string>();
  existing.add(docKey);
  exact.set(normalized, existing);
}

function buildExactMap(snapshot: FrameworkSnapshot, assets: readonly SearchAssetInput[]): Record<string, string[]> {
  const exact = new Map<string, Set<string>>();
  for (const entity of snapshot.groundworks.entities) {
    const docKey = `groundworks-record-${entity.id}`;
    addExact(exact, entity.id, docKey);
    for (const field of entity.fields) {
      /* Field ids are many-to-many across entities: the exact tier returns
       * the ranked owning-entity set and never short-circuits to one row. */
      addExact(exact, field.id, docKey);
      for (const mapping of field.standardMappings) addExact(exact, mapping.path, docKey);
    }
  }
  for (const businessModule of snapshot.businessModuleRegistry.businessModules) {
    addExact(exact, businessModule.id, `business-module-${businessModule.id}`);
    for (const flow of businessModule.executionFlows) {
      addExact(exact, flow.id, `execution-flow-${businessModule.id}-${flow.id}`);
    }
  }
  for (const domainWorkblock of snapshot.content.domainWorkblocks) {
    addExact(exact, domainWorkblock.id, `workblock-${domainWorkblock.id}`);
  }
  for (const role of snapshot.content.roles ?? []) addExact(exact, role.id, `role-${role.id}`);
  for (const asset of assets) addExact(exact, asset.assetId, `download-asset-${asset.assetId}`);
  const serialised: Record<string, string[]> = {};
  for (const key of [...exact.keys()].sort()) {
    serialised[key] = [...(exact.get(key) ?? [])].sort();
  }
  return serialised;
}

export interface BuildSearchIndexInput {
  snapshot: FrameworkSnapshot;
  /** Downloadable-asset payloads; every asset must already carry audience "site". */
  assets?: readonly SearchAssetInput[];
}

export function buildSearchIndex({ snapshot, assets = [] }: BuildSearchIndexInput): SearchIndexArtifact {
  const copy: SearchCopyResolver = (key, fallback) => snapshot.copy[key] ?? fallback ?? key;
  const documents = [
    ...buildSnapshotDocuments(snapshot, copy),
    ...buildGlossaryTermDocuments(snapshot, copy),
    ...buildEditorialChapterDocuments(snapshot, copy),
  ];

  const orderedAssets = [...assets].sort((left, right) =>
    left.assetId < right.assetId ? -1 : left.assetId > right.assetId ? 1 : 0,
  );
  const assetDocuments: AssetSearchDocument[] = [];
  const assetTokens = new Map<string, Set<number>>();
  for (const asset of orderedAssets) {
    const index = assetDocuments.length;
    const body = asset.texts.join("\n");
    assetDocuments.push({
      key: `download-asset-${asset.assetId}`,
      label: asset.title,
      excerpt: buildExcerpt(body),
      context: humanize(asset.assetClass),
      to: asset.route,
      icon: "files",
    });
    for (const token of tokenizeSearchText(`${asset.title}\n${asset.assetClass}\n${body}`)) {
      const postings = assetTokens.get(token) ?? new Set<number>();
      postings.add(index);
      assetTokens.set(token, postings);
    }
  }
  const postings: Record<string, number[]> = {};
  for (const token of [...assetTokens.keys()].sort()) {
    postings[token] = [...(assetTokens.get(token) ?? [])].sort((a, b) => a - b);
  }

  return {
    doNotEdit: true,
    schemaVersion: searchIndexSchemaVersion,
    audienceId: snapshot.audienceId,
    sourceContentHash: snapshot.generated.contentHash,
    documents,
    assetDocuments,
    assetTokens: postings,
    exact: buildExactMap(snapshot, assets),
  };
}

/* ------------------------------------------------------------------------ *
 * The ranker. scoreEntry is the in-page ladder moved verbatim so ranking is
 * provably unchanged; body hits score +1, the same weight as a description,
 * so a governed title always outranks a body hit.
 * ------------------------------------------------------------------------ */

/**
 * Labels outrank descriptions: a term that opens a label places that result
 * above one that only mentions it in passing. Every token must match somewhere.
 */
export function scoreEntry(
  entry: Pick<SearchDocument, "label" | "keywords" | "description">,
  tokens: string[],
): number {
  const label = entry.label.toLowerCase();
  const keywords = (entry.keywords ?? "").toLowerCase();
  const description = (entry.description ?? "").toLowerCase();
  let total = 0;
  for (const token of tokens) {
    if (label.startsWith(token)) total += 8;
    else if (label.includes(token)) total += 5;
    else if (keywords.includes(token)) total += 2;
    else if (description.includes(token)) total += 1;
    else return 0;
  }
  return total;
}

function assetDocIndexesForToken(artifact: SearchIndexArtifact, token: string): Set<number> {
  const matches = new Set<number>();
  for (const [candidate, docIndexes] of Object.entries(artifact.assetTokens)) {
    if (candidate === token || candidate.startsWith(token)) {
      for (const index of docIndexes) matches.add(index);
    }
  }
  return matches;
}

export interface QuerySearchIndexOptions {
  referenceOnly?: boolean;
}

/**
 * One ranker for every surface. Matches are returned in stable document
 * order; display sorting (score, then locale-aware label order) stays at
 * render time with the snapshot's locale.
 */
export function querySearchIndex(
  artifact: SearchIndexArtifact,
  query: string,
  { referenceOnly = false }: QuerySearchIndexOptions = {},
): SearchIndexMatch[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length && !referenceOnly) return [];
  const exactKeys = new Set(artifact.exact[query.trim().toLowerCase()] ?? []);
  const matches: SearchIndexMatch[] = [];

  for (const document of artifact.documents) {
    if (
      referenceOnly &&
      !(document.group === SEARCH_GROUP.businessModules && document.status === "reference")
    ) {
      continue;
    }
    const base = tokens.length ? scoreEntry(document, tokens) : 1;
    const score = tokens.length && exactKeys.has(document.key) ? base + exactTermScore : base;
    if (score > 0) matches.push({ entry: document, score });
  }

  if (!referenceOnly && tokens.length) {
    const tokenMatches = tokens.map((token) => assetDocIndexesForToken(artifact, token));
    artifact.assetDocuments.forEach((assetDocument, index) => {
      const label = assetDocument.label.toLowerCase();
      let total = 0;
      for (let position = 0; position < tokens.length; position += 1) {
        const token = tokens[position]!;
        if (label.startsWith(token)) total += 8;
        else if (label.includes(token)) total += 5;
        else if (tokenMatches[position]!.has(index)) total += 1;
        else return;
      }
      const score = exactKeys.has(assetDocument.key) ? total + exactTermScore : total;
      matches.push({
        entry: {
          key: assetDocument.key,
          group: SEARCH_GROUP.documents,
          label: assetDocument.label,
          description: assetDocument.excerpt,
          context: assetDocument.context,
          to: assetDocument.to,
          icon: assetDocument.icon,
        },
        score,
      });
    });
  }

  return matches;
}
